/**
 * Mensagens seguidas viram UM resumo de conversa (H08).
 *
 * ── O que estava errado ───────────────────────────────────────────────────
 *
 * A linha do tempo listava cada mensagem como um fato. Um atendimento normal
 * tem trinta ou quarenta — e o cliente que voltou três vezes no mês enche a
 * tela com cento e vinte linhas de "mensagem · 14:13 · trecho".
 *
 * O efeito é o oposto do que a timeline existe para dar: ela serve para ver a
 * SEQUÊNCIA — a mensagem que veio antes da mudança de etapa, a tarefa que
 * nasceu depois da ligação. Com cento e vinte linhas de mensagem no meio, a
 * mudança de etapa fica a três rolagens da conversa que a causou.
 *
 * ── O resumo, e por que esses campos ──────────────────────────────────────
 *
 * Quem conversou, de que hora a que hora, e quantas mensagens. É o que responde
 * "houve conversa aqui?" sem obrigar a ler a conversa — e quem quiser ler abre,
 * que é um clique que já existia.
 *
 * ── Só o que é CONSECUTIVO, e só da mesma conversa ────────────────────────
 *
 * Duas regras, e as duas preservam a sequência:
 *
 *   1. mensagens de conversas diferentes nunca se juntam, mesmo lado a lado —
 *      são dois assuntos;
 *   2. um fato de outro tipo no meio CORTA o bloco. Se a etapa mudou entre a
 *      décima e a décima primeira mensagem, o resumo vira dois, com a mudança
 *      entre eles. Juntar tudo esconderia justamente a ordem que interessa.
 *
 * ── Uma mensagem sozinha continua uma mensagem ────────────────────────────
 *
 * Resumir uma leria "das 14:12 às 14:12, 1 mensagem", que é mais palavras para
 * dizer menos. Abaixo de duas, o item passa direto.
 */

import type { ItemDaTimeline } from '@cliqcrm/contracts';

export interface ResumoDeConversa {
  readonly tipo: 'resumo-de-conversa';
  readonly conversaId: string;
  /** O instante mais antigo do bloco. */
  readonly de: string;
  /** O mais recente. */
  readonly ate: string;
  readonly quantidade: number;
  /** Quem apareceu como ator, sem repetir e na ordem em que apareceram. */
  readonly atores: readonly string[];
  /** O trecho da mensagem mais recente do bloco — a deixa de onde parou. */
  readonly ultimoTrecho: string | null;
}

export type ItemOuResumo = ItemDaTimeline | ResumoDeConversa;

export function ehResumo(item: ItemOuResumo): item is ResumoDeConversa {
  return (item as ResumoDeConversa).tipo === 'resumo-de-conversa';
}

/** Mínimo de mensagens para virar resumo. Abaixo disso, o item passa direto. */
const MINIMO_PARA_RESUMIR = 2;

export function resumirMensagens(itens: readonly ItemDaTimeline[]): readonly ItemOuResumo[] {
  const saida: ItemOuResumo[] = [];
  let bloco: ItemDaTimeline[] = [];

  const fechar = (): void => {
    if (bloco.length === 0) return;
    saida.push(bloco.length >= MINIMO_PARA_RESUMIR ? montarResumo(bloco) : bloco[0]!);
    bloco = [];
  };

  for (const item of itens) {
    const agrupavel = item.tipo === 'mensagem' && item.conversa_id !== null;
    const mesmaConversa = bloco.length > 0 && bloco[0]!.conversa_id === item.conversa_id;

    if (agrupavel && (bloco.length === 0 || mesmaConversa)) {
      bloco.push(item);
      continue;
    }

    fechar();
    if (agrupavel) bloco.push(item);
    else saida.push(item);
  }

  fechar();
  return saida;
}

function montarResumo(bloco: readonly ItemDaTimeline[]): ResumoDeConversa {
  /*
   * `de` e `ate` saem do MENOR e do MAIOR, e não do primeiro e do último.
   *
   * A timeline chega do mais novo para o mais antigo, e vai mudar de ordem no
   * dia em que alguém inverter o filtro. Ler as pontas pela posição faria o
   * resumo dizer "das 14:47 às 14:12" — que parece defeito de relógio.
   */
  const instantes = bloco.map((item) => item.ocorrido_em).sort();

  const atores: string[] = [];
  // Ordem de aparição, sem repetir: numa conversa de trinta mensagens são duas
  // ou três pessoas, e a ordem conta quem começou.
  for (let i = bloco.length - 1; i >= 0; i -= 1) {
    const ator = bloco[i]!.ator;
    if (ator && !atores.includes(ator)) atores.push(ator);
  }

  const maisRecente = bloco.reduce((a, b) => (a.ocorrido_em >= b.ocorrido_em ? a : b));

  return {
    tipo: 'resumo-de-conversa',
    conversaId: bloco[0]!.conversa_id as string,
    de: instantes[0]!,
    ate: instantes[instantes.length - 1]!,
    quantidade: bloco.length,
    atores,
    ultimoTrecho: maisRecente.trecho,
  };
}

/**
 * "Gabriel e Maria", "Gabriel, Maria e mais 2" — e vazio quando não há ator.
 *
 * O motor nem sempre diz quem falou (mensagem de sistema, automação), e
 * inventar "alguém" seria pior que a ausência: a frase do resumo continua
 * inteira sem essa parte.
 */
export function atoresEmPalavras(atores: readonly string[]): string {
  if (atores.length === 0) return '';
  if (atores.length === 1) return atores[0]!;
  if (atores.length === 2) return `${atores[0]} e ${atores[1]}`;
  return `${atores[0]}, ${atores[1]} e mais ${atores.length - 2}`;
}
